/**
 * Accessibility Utilities (T172)
 *
 * Helper functions and utilities for implementing accessible features
 * in the Yi Dashboard application.
 *
 * Features:
 * - Unique ID generation for aria-labelledby connections
 * - Live region announcements for screen readers
 * - Focus trap utilities for modal dialogs
 * - Keyboard navigation helpers
 * - Skip link management
 *
 * @see https://www.w3.org/WAI/WCAG21/quickref/
 */

/**
 * Counter for generating unique IDs.
 */
let idCounter = 0;

/**
 * Generate a unique ID for aria-labelledby and similar connections.
 *
 * @param prefix - Optional prefix for the ID (default: 'a11y')
 * @returns A unique string ID
 *
 * @example
 * ```tsx
 * const labelId = generateId('modal-title');
 * const descId = generateId('modal-desc');
 *
 * <div
 *   role="dialog"
 *   aria-labelledby={labelId}
 *   aria-describedby={descId}
 * >
 *   <h2 id={labelId}>Modal Title</h2>
 *   <p id={descId}>Modal description text.</p>
 * </div>
 * ```
 */
export function generateId(prefix = 'a11y'): string {
  return `${prefix}-${++idCounter}`;
}

/**
 * Reset the ID counter (useful for testing).
 */
export function resetIdCounter(): void {
  idCounter = 0;
}

/**
 * Live region element reference for announcements.
 */
let liveRegion: HTMLElement | null = null;

/**
 * Initialize the live region element for screen reader announcements.
 * Should be called once during application startup.
 *
 * @returns The live region element
 *
 * @example
 * ```tsx
 * // In root.tsx or App component
 * useEffect(() => {
 *   initLiveRegion();
 *   return () => destroyLiveRegion();
 * }, []);
 * ```
 */
export function initLiveRegion(): HTMLElement | null {
  if (typeof document === 'undefined' || !document.body) return null;

  // Check if already initialized
  if (liveRegion && document.body.contains(liveRegion)) {
    return liveRegion;
  }

  // Create live region element
  liveRegion = document.createElement('div');
  liveRegion.id = 'a11y-live-region';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');

  // Visually hidden but accessible to screen readers
  Object.assign(liveRegion.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
  });

  document.body.appendChild(liveRegion);
  return liveRegion;
}

/**
 * Destroy the live region element.
 * Safely handles cases where document.body may be null during hydration errors.
 */
export function destroyLiveRegion(): void {
  if (typeof document === 'undefined' || !document.body) {
    liveRegion = null;
    return;
  }
  if (liveRegion && document.body.contains(liveRegion)) {
    document.body.removeChild(liveRegion);
    liveRegion = null;
  }
}

/**
 * Announce a message to screen readers using the live region.
 *
 * @param message - The message to announce
 * @param priority - Priority level: 'polite' (default) or 'assertive'
 *
 * @example
 * ```tsx
 * // Announce successful save
 * announceToScreenReader('Episode saved successfully');
 *
 * // Announce error with high priority
 * announceToScreenReader('Error: Failed to save episode', 'assertive');
 * ```
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  if (typeof document === 'undefined') return;

  // Initialize if needed
  if (!liveRegion) {
    initLiveRegion();
  }

  if (!liveRegion) return;

  // Update aria-live for priority
  liveRegion.setAttribute('aria-live', priority);

  // Clear then set message (triggers announcement)
  liveRegion.textContent = '';

  // Use requestAnimationFrame to ensure the clear is processed
  requestAnimationFrame(() => {
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  });
}

/**
 * Get all focusable elements within a container.
 *
 * @param container - The container element
 * @returns Array of focusable elements
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  const elements = container.querySelectorAll<HTMLElement>(focusableSelectors);
  return Array.from(elements).filter((el) => {
    // Check if element is visible
    const style = window.getComputedStyle(el);
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      !el.hasAttribute('inert')
    );
  });
}

/**
 * Focus trap configuration options.
 */
export interface FocusTrapOptions {
  /** Initial element to focus (defaults to first focusable) */
  initialFocus?: HTMLElement | null;
  /** Element to return focus to on deactivation */
  returnFocus?: HTMLElement | null;
  /** Whether to close on Escape key */
  escapeDeactivates?: boolean;
  /** Callback when escape is pressed */
  onEscape?: () => void;
}

/**
 * Focus trap state.
 */
interface FocusTrapState {
  container: HTMLElement;
  options: FocusTrapOptions;
  previousActiveElement: Element | null;
  handleKeyDown: (event: KeyboardEvent) => void;
}

/**
 * Active focus traps (for nested modals).
 */
const activeFocusTraps: FocusTrapState[] = [];

/**
 * Create and activate a focus trap within a container.
 * The focus trap keeps focus cycling within the container.
 *
 * @param container - The container element to trap focus within
 * @param options - Focus trap options
 * @returns Deactivation function
 *
 * @example
 * ```tsx
 * const deactivate = createFocusTrap(modalRef.current, {
 *   onEscape: () => setIsOpen(false),
 *   returnFocus: triggerButtonRef.current,
 * });
 *
 * // Later, when closing the modal
 * deactivate();
 * ```
 */
export function createFocusTrap(
  container: HTMLElement,
  options: FocusTrapOptions = {}
): () => void {
  const {
    initialFocus = null,
    returnFocus = null,
    escapeDeactivates = true,
    onEscape,
  } = options;

  // Store the previously focused element
  const previousActiveElement = document.activeElement;

  // Handle keyboard navigation
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && escapeDeactivates) {
      event.preventDefault();
      onEscape?.();
      return;
    }

    if (event.key === 'Tab') {
      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift+Tab: go backwards
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: go forwards
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  // Create trap state
  const trapState: FocusTrapState = {
    container,
    options,
    previousActiveElement,
    handleKeyDown,
  };

  // Add to active traps
  activeFocusTraps.push(trapState);

  // Add keyboard listener
  container.addEventListener('keydown', handleKeyDown);

  // Focus initial element
  const focusableElements = getFocusableElements(container);
  if (initialFocus) {
    initialFocus.focus();
  } else if (focusableElements.length > 0) {
    focusableElements[0].focus();
  }

  // Return deactivation function
  return () => {
    // Remove from active traps
    const index = activeFocusTraps.indexOf(trapState);
    if (index > -1) {
      activeFocusTraps.splice(index, 1);
    }

    // Remove keyboard listener
    container.removeEventListener('keydown', handleKeyDown);

    // Return focus to previous element or specified return element
    const focusTarget = returnFocus || previousActiveElement;
    if (focusTarget && focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }
  };
}

/**
 * Keyboard navigation key codes.
 */
export const Keys = {
  Enter: 'Enter',
  Space: ' ',
  Escape: 'Escape',
  Tab: 'Tab',
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
  Home: 'Home',
  End: 'End',
  PageUp: 'PageUp',
  PageDown: 'PageDown',
} as const;

/**
 * Check if a keyboard event matches a specific key.
 *
 * @param event - Keyboard event
 * @param key - Key to check
 * @returns Whether the event matches the key
 */
export function isKey(
  event: KeyboardEvent,
  key: keyof typeof Keys | (keyof typeof Keys)[]
): boolean {
  const keys = Array.isArray(key) ? key : [key];
  return keys.some((k) => event.key === Keys[k]);
}

/**
 * Handle keyboard navigation for a list of items.
 *
 * @param event - Keyboard event
 * @param items - List of items (must have focus() method)
 * @param currentIndex - Current focused index
 * @param options - Navigation options
 * @returns New index, or -1 if not handled
 *
 * @example
 * ```tsx
 * const handleKeyDown = (event: KeyboardEvent) => {
 *   const newIndex = handleListNavigation(
 *     event,
 *     listItemRefs.current,
 *     currentIndex,
 *     { wrap: true, orientation: 'vertical' }
 *   );
 *   if (newIndex !== -1) {
 *     setCurrentIndex(newIndex);
 *   }
 * };
 * ```
 */
export function handleListNavigation(
  event: KeyboardEvent,
  items: (HTMLElement | null)[],
  currentIndex: number,
  options: {
    wrap?: boolean;
    orientation?: 'horizontal' | 'vertical' | 'both';
    onSelect?: (index: number) => void;
  } = {}
): number {
  const { wrap = true, orientation = 'vertical', onSelect } = options;

  const validItems = items.filter((item): item is HTMLElement => item !== null);
  if (validItems.length === 0) return -1;

  const isVertical = orientation === 'vertical' || orientation === 'both';
  const isHorizontal = orientation === 'horizontal' || orientation === 'both';

  let newIndex = currentIndex;

  switch (event.key) {
    case Keys.ArrowUp:
      if (!isVertical) return -1;
      event.preventDefault();
      newIndex = currentIndex - 1;
      if (newIndex < 0) {
        newIndex = wrap ? validItems.length - 1 : 0;
      }
      break;

    case Keys.ArrowDown:
      if (!isVertical) return -1;
      event.preventDefault();
      newIndex = currentIndex + 1;
      if (newIndex >= validItems.length) {
        newIndex = wrap ? 0 : validItems.length - 1;
      }
      break;

    case Keys.ArrowLeft:
      if (!isHorizontal) return -1;
      event.preventDefault();
      newIndex = currentIndex - 1;
      if (newIndex < 0) {
        newIndex = wrap ? validItems.length - 1 : 0;
      }
      break;

    case Keys.ArrowRight:
      if (!isHorizontal) return -1;
      event.preventDefault();
      newIndex = currentIndex + 1;
      if (newIndex >= validItems.length) {
        newIndex = wrap ? 0 : validItems.length - 1;
      }
      break;

    case Keys.Home:
      event.preventDefault();
      newIndex = 0;
      break;

    case Keys.End:
      event.preventDefault();
      newIndex = validItems.length - 1;
      break;

    case Keys.Enter:
    case Keys.Space:
      event.preventDefault();
      onSelect?.(currentIndex);
      return currentIndex;

    default:
      return -1;
  }

  // Focus the new item
  validItems[newIndex]?.focus();
  return newIndex;
}

/**
 * Scroll an element into view if it's not visible.
 *
 * @param element - Element to scroll into view
 * @param container - Scrollable container (defaults to document.documentElement)
 */
export function scrollIntoViewIfNeeded(
  element: HTMLElement,
  container?: HTMLElement
): void {
  const scrollParent = container || document.documentElement;
  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollParent.getBoundingClientRect();

  if (
    elementRect.top < containerRect.top ||
    elementRect.bottom > containerRect.bottom
  ) {
    element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

/**
 * Create an ID pair for label/input connections.
 *
 * @param prefix - Prefix for the IDs
 * @returns Object with inputId and labelId
 *
 * @example
 * ```tsx
 * const { inputId, labelId } = createLabelConnection('email');
 *
 * <label id={labelId} htmlFor={inputId}>Email</label>
 * <input id={inputId} aria-labelledby={labelId} />
 * ```
 */
export function createLabelConnection(prefix: string): {
  inputId: string;
  labelId: string;
  descriptionId: string;
  errorId: string;
} {
  const baseId = generateId(prefix);
  return {
    inputId: `${baseId}-input`,
    labelId: `${baseId}-label`,
    descriptionId: `${baseId}-description`,
    errorId: `${baseId}-error`,
  };
}

/**
 * Check if the user prefers reduced motion.
 *
 * @returns Whether reduced motion is preferred
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Skip link management for main content navigation.
 */
export const SkipLink = {
  /**
   * Handle skip link click - focuses the main content.
   *
   * @param event - Click event
   * @param targetId - ID of the target element (default: 'main-content')
   */
  handleClick(event: React.MouseEvent, targetId = 'main-content'): void {
    event.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      // Set tabindex if not focusable
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus();
      // Announce for screen readers
      announceToScreenReader('Skipped to main content');
    }
  },

  /**
   * CSS class for visually hidden skip link.
   * The link is visible only when focused.
   */
  className: `
    absolute left-[-9999px] z-[9999] p-4 bg-background text-foreground
    focus:left-0 focus:top-0 focus:outline-2 focus:outline-offset-2
    focus:outline-ring
  `.trim(),
} as const;

/**
 * Accessibility documentation and patterns.
 *
 * COLOR CONTRAST REQUIREMENTS:
 * - Normal text (< 18px): 4.5:1 contrast ratio (WCAG AA)
 * - Large text (>= 18px or 14px bold): 3:1 contrast ratio (WCAG AA)
 * - UI components and graphics: 3:1 contrast ratio
 *
 * FOCUS MANAGEMENT:
 * - All interactive elements must have visible focus indicators
 * - Focus should be managed when opening/closing modals
 * - Use focus traps for modal dialogs
 * - Return focus to trigger element when closing modals
 *
 * KEYBOARD NAVIGATION:
 * - All functionality must be accessible via keyboard
 * - Tab order should be logical and predictable
 * - Use arrow keys for navigation within components (menus, tabs, etc.)
 * - Escape should close modals and popups
 *
 * SCREEN READER SUPPORT:
 * - Use semantic HTML (nav, main, article, aside, etc.)
 * - Provide text alternatives for images (alt text)
 * - Use ARIA roles and properties appropriately
 * - Announce dynamic content changes via live regions
 *
 * TESTING NOTES:
 * - Test with keyboard only (no mouse)
 * - Test with screen readers (VoiceOver, NVDA)
 * - Test with browser zoom (up to 200%)
 * - Test color contrast with tools like axe-core
 * - Run automated accessibility audits (Lighthouse, axe)
 */
export const A11Y_DOCUMENTATION = {
  colorContrast: {
    normalText: '4.5:1',
    largeText: '3:1',
    uiComponents: '3:1',
  },
  focusManagement: [
    'Visible focus indicators on all interactive elements',
    'Focus traps for modal dialogs',
    'Return focus to trigger on modal close',
  ],
  keyboardNavigation: [
    'All functionality keyboard accessible',
    'Logical tab order',
    'Arrow keys for component navigation',
    'Escape to close modals',
  ],
  screenReaderSupport: [
    'Semantic HTML elements',
    'Alt text for images',
    'ARIA roles and properties',
    'Live region announcements',
  ],
  testingChecklist: [
    'Keyboard-only navigation',
    'Screen reader testing (VoiceOver, NVDA)',
    'Browser zoom (up to 200%)',
    'Color contrast verification',
    'Automated audits (Lighthouse, axe)',
  ],
} as const;
